
import classNames from 'classnames';
import PropTypes from 'prop-types';
import React, { useMemo, useState } from 'react';
import { FormattedMessage } from 'react-intl';

import { Button } from '@micromag/core/components';

import { useMediaPipelines } from '../../contexts/MediaPipelinesContext';
import useMediaJobCreate from '../../hooks/useMediaJobCreate';
import useMediaJobRestart from '../../hooks/useMediaJobRestart';
import MediaJobItem from '../items/MediaJob';
import { mediaJobs } from '../../lib/PropTypes';

import styles from '../../../styles/lists/media-jobs.module.scss';

const propTypes = {
    value: mediaJobs,
    mediaType: PropTypes.string,
    mediaId: PropTypes.string,
    maxItemCount: PropTypes.number,
    className: PropTypes.string,
};

const defaultProps = {
    value: null,
    mediaType: null,
    mediaId: null,
    maxItemCount: 10,
    className: null,
};

const MediaJobsList = ({ value, mediaType, mediaId, maxItemCount, className }) => {
    const { mediaPipelines } = useMediaPipelines();
    const { create: createMediaJob } = useMediaJobCreate();
    const { restart: restartMediaJob } = useMediaJobRestart();
    const [dropdownMenuVisible, setDropdownMenuVisible] = useState(false);

    const filteredJobs = useMemo(() => {
        if (!value) {
            return [];
        }

        return Array.from(value)
            .sort(({ created_on: aDate }, { created_on: bDate }) => bDate.localeCompare(aDate))
            .slice(0, maxItemCount);
    }, [value])

    const filteredMediaPipelines = useMemo(
        () => {
            if (!mediaType) {
                return mediaPipelines;
            }

            return (mediaPipelines || []).filter(mediaPipeline => {
                const applicableTypes = mediaPipeline.applicable_types;
                if (!applicableTypes || !applicableTypes.length) {
                    return true; // null value or empty array => match all types
                }

                if (applicableTypes.includes(mediaType)) {
                    return true;
                }

                return false;
            })
        },
        [mediaPipelines, mediaType]
    )

    const onStartTaskClicked = (mediaPipeline) => {
        if (!mediaId) {
            return;
        }

        createMediaJob(mediaId, mediaPipeline.name);
        setDropdownMenuVisible(false);
    }

    const onRestartTaskClicked = (jobId) => {
        if (!mediaId) {
            return;
        }

        restartMediaJob(mediaId, jobId);
    }

    return (
        <div className={classNames(
            styles.container,
            {
                [className]: className !== null
            }
        )}>
            {(filteredJobs.length) ? (
                <ul className='list-group'>
                    {filteredJobs.map((job) =>
                        <MediaJobItem key={job.id} job={job} onRestartClicked={onRestartTaskClicked} />
                    )}
                </ul>
            ) : <div className={classNames('p-2', 'text-center', 'fst-italic')}>
                <FormattedMessage defaultMessage='Aucune tâche à afficher' description='MediaJobsList - empty list placeholder' />
            </div>}

            <div className={classNames('mt-3', 'd-flex')}>
                <div className='dropdown'>
                    <Button
                        className={classNames('btn', 'btn-primary', 'px-2', 'py-1', 'dropdown-toggle')}
                        type="button"
                        data-bs-toggle="dropdown"
                        onClick={() => setDropdownMenuVisible(!dropdownMenuVisible)}
                        disabled={!mediaId || !filteredMediaPipelines.length}
                    >
                        <FormattedMessage defaultMessage='Lancer une tâche' description='MediaJobsList - "start task" button' />
                    </Button>
                    <ul className={classNames('dropdown-menu', {
                        [styles.dropdownMenuVisible]: dropdownMenuVisible
                    })}>
                        {(filteredMediaPipelines || []).map(mediaPipeline => (
                            <li key={mediaPipeline.name}>
                                <button
                                    type='button'
                                    className={classNames('dropdown-item', styles.dropdownItem)}
                                    onClick={() => onStartTaskClicked(mediaPipeline)}
                                >{mediaPipeline.display_name || mediaPipeline.name}</button>
                            </li>
                        ))}
                    </ul>
                </div>
            </div>
        </div>
    );
}

MediaJobsList.propTypes = propTypes;
MediaJobsList.defaultProps = defaultProps;

export default MediaJobsList;
